import { combineReducers } from '@reduxjs/toolkit';

import { acceptCreditLimitDataModule } from '@perpay-web/fintech/dataModules/acceptCreditLimit';
import { cancelAutoPayScheduleDataModule } from '@perpay-web/fintech/dataModules/cancelAutoPaySchedule';
import {
    freezeCardDataModule,
    unfreezeCardDataModule,
} from '@perpay-web/fintech/dataModules/cardFreeze';
import { borrowerCreditsDataModule } from '@perpay-web/fintech/dataModules/composed/borrowerCredits';
import { cardActivityDataModule } from '@perpay-web/fintech/dataModules/composed/cardActivity';
import { cardCreateAutoPayDataModule } from '@perpay-web/fintech/dataModules/composed/cardCreateAutoPay';
import { cardManualPaymentSetupDataModule } from '@perpay-web/fintech/dataModules/composed/cardManualPaymentSetup';
import { cardOnboardingSwitcherDataModule } from '@perpay-web/fintech/dataModules/composed/cardOnboardingSwitcher';
import { cardPaymentSummaryDataModule } from '@perpay-web/fintech/dataModules/composed/cardPaymentSummary';
import { cardStatementsDataModule } from '@perpay-web/fintech/dataModules/composed/cardStatements';
import { checkoutDataModule } from '@perpay-web/fintech/dataModules/composed/checkout';
import { dashboardDataModule as dashboardV2DataModule } from '@perpay-web/fintech/dataModules/composed/dashboardV2';
import { directDepositDetailsDataModule } from '@perpay-web/fintech/dataModules/composed/directDepositDetails';
import { directDepositUpdateEmployerAndAmountDataModule } from '@perpay-web/fintech/dataModules/composed/directDepositUpdateEmployerAndAmount';
import { directDepositUpdateManualPaymentSetupDataModule } from '@perpay-web/fintech/dataModules/composed/directDepositUpdateManualPaymentSetup';
import { fetchCardDetailsV2DataModule } from '@perpay-web/fintech/dataModules/composed/fetchCardDetailsV2';
import { marketplaceDetailsDataModule } from '@perpay-web/fintech/dataModules/composed/marketplaceDetails';
import { ordersDataModule } from '@perpay-web/fintech/dataModules/composed/orders';
import { partnerOnboardingDataModule } from '@perpay-web/fintech/dataModules/composed/partnerOnboarding';
import { payrollInstructionsFlowDataModule } from '@perpay-web/fintech/dataModules/composed/payrollInstructionsFlow';
import { perpayPlusDataModule } from '@perpay-web/fintech/dataModules/composed/perpayPlus';
import { perpayPlusSoloDataModule } from '@perpay-web/fintech/dataModules/composed/perpayPlusSolo';
import { perpayPlusSoloApplicationDataModule } from '@perpay-web/fintech/dataModules/composed/perpayPlusSoloApplication';
import { pinwheelDataModule } from '@perpay-web/fintech/dataModules/composed/pinwheel';
import { updateDirectDepositDataModule } from '@perpay-web/fintech/dataModules/composed/updateDirectDeposit';
import { coreSummaryDataModule } from '@perpay-web/fintech/dataModules/coreSummary';
import { createAutoPayScheduleDataModule } from '@perpay-web/fintech/dataModules/createAutoPaySchedule';
import { createAutoPayScheduleForCardAutoPayDataModule } from '@perpay-web/fintech/dataModules/createAutoPayScheduleForCardAutoPay';
import { createBankAccountDataModule } from '@perpay-web/fintech/dataModules/createBankAccount';
import { createBankAccountForCardAutoPayDataModule } from '@perpay-web/fintech/dataModules/createBankAccountForCardAutoPay';
import { createCardAccountApplicationsDataModule } from '@perpay-web/fintech/dataModules/createCardAccountApplications';
import { createFeatureEnrollmentDataModule } from '@perpay-web/fintech/dataModules/createFeatureEnrollment';
import { createPrimaryJobDataModule } from '@perpay-web/fintech/dataModules/createPrimaryJob';
import { creditUnfreezeForCardAccountApplicationDataModule } from '@perpay-web/fintech/dataModules/creditUnfreezeCardAccountApplication';
import { bankAccountsForCardAutoPayDataModule } from '@perpay-web/fintech/dataModules/crud/bankAccountsForCardAutoPay';
import { cardAccountApplicationCrudDataModule } from '@perpay-web/fintech/dataModules/crud/cardAccountApplication';
import { userInfoForCardOnboardingMVP2CrudDataModule } from '@perpay-web/fintech/dataModules/crud/userInfoForCardOnboardingMVP2';
import { downloadCardStatementDataModule } from '@perpay-web/fintech/dataModules/downloadCardStatement';
import { fetchABTestsDataModule } from '@perpay-web/fintech/dataModules/fetchABTests';
import { fetchActivePaymentsDataModule } from '@perpay-web/fintech/dataModules/fetchActivePayments';
import { fetchAutoPaySchedulesDataModule } from '@perpay-web/fintech/dataModules/fetchAutoPaySchedules';
import { fetchBankAccountsDataModule } from '@perpay-web/fintech/dataModules/fetchBankAccounts';
import { fetchBankAccountsForCardAutoPayDataModule } from '@perpay-web/fintech/dataModules/fetchBankAccountsForCardAutoPay';
import { fetchBorrowerCreditSummaryDataModule } from '@perpay-web/fintech/dataModules/fetchBorrowerCreditSummary';
import { fetchBorrowerCreditsDataModule } from '@perpay-web/fintech/dataModules/fetchBorrowerCredits';
import { fetchCardAccountApplicationForOnboardingDataModule } from '@perpay-web/fintech/dataModules/fetchCardAccountApplicationForOnboarding';
import { fetchCardAccountApplicationDataModule } from '@perpay-web/fintech/dataModules/fetchCardAccountApplications';
import { fetchCardAccountsDataModule } from '@perpay-web/fintech/dataModules/fetchCardAccounts';
import { fetchCardActivationDataModule } from '@perpay-web/fintech/dataModules/fetchCardActivation';
import { fetchCardActivityDataModule } from '@perpay-web/fintech/dataModules/fetchCardActivity';
import { fetchCardDashboardSummaryDataModule } from '@perpay-web/fintech/dataModules/fetchCardDashboardSummary';
import { fetchCardStatementsDataModule } from '@perpay-web/fintech/dataModules/fetchCardStatements';
import { fetchCreditLimitsDataModule } from '@perpay-web/fintech/dataModules/fetchCreditLimits';
import { fetchCreditScoreHistoryDataModule } from '@perpay-web/fintech/dataModules/fetchCreditScoreHistory';
import { fetchDeductionsDataModule } from '@perpay-web/fintech/dataModules/fetchDeductions';
import { fetchDirectDepositDashboardSummaryDataModule } from '@perpay-web/fintech/dataModules/fetchDirectDepositDashboardSummary';
import { fetchFeatureEnrollmentsDataModule } from '@perpay-web/fintech/dataModules/fetchFeatureEnrollments';
import { fetchFeatureEnrollmentsForPerpayPlusDataModule } from '@perpay-web/fintech/dataModules/fetchFeatureEnrollmentsForPerpayPlus';
import { fetchFeatureSwitchesDataModule } from '@perpay-web/fintech/dataModules/fetchFeatureSwitches';
import { fetchFeaturesForOnboardingDataModule } from '@perpay-web/fintech/dataModules/fetchFeaturesForOnboarding';
import { fetchInitiatedAutoPayPaymentsDataModule } from '@perpay-web/fintech/dataModules/fetchInitiatedAutoPayPayments';
import { fetchJobsDataModule } from '@perpay-web/fintech/dataModules/fetchJobs';
import { fetchLearnMoreDataModule } from '@perpay-web/fintech/dataModules/fetchLearnMore';
import { fetchMarketplaceDashboardSummaryDataModule } from '@perpay-web/fintech/dataModules/fetchMarketplaceDashboardSummary';
import { fetchOrdersDataModule } from '@perpay-web/fintech/dataModules/fetchOrders';
import { fetchPaymentSetupSummaryDataModule } from '@perpay-web/fintech/dataModules/fetchPaymentSetupSummary';
import { fetchPayrollInstructionsDataModule } from '@perpay-web/fintech/dataModules/fetchPayrollInstructions';
import { fetchPerpayPlusDashboardSummaryDataModule } from '@perpay-web/fintech/dataModules/fetchPerpayPlusDashboardSummary';
import { fetchPinwheelABTestDataModule } from '@perpay-web/fintech/dataModules/fetchPinwheelABTest';
import { fetchPlatformDashboardNotificationsDataModule } from '@perpay-web/fintech/dataModules/fetchPlatformDashboardNotifications';
import { fetchTransactionDetailsDataModule } from '@perpay-web/fintech/dataModules/fetchTransactionDetails';
import { fetchTrustPilotLinkDataModule } from '@perpay-web/fintech/dataModules/fetchTrustPilotLink';
import { fetchUserInfoDataModule } from '@perpay-web/fintech/dataModules/fetchUserInfo';
import { fetchUserStatusDataModule } from '@perpay-web/fintech/dataModules/fetchUserStatus';
import { fetchWithdrawalBalanceV2DataModule } from '@perpay-web/fintech/dataModules/fetchWithdrawalBalance';
import { gateCardAccountDataModule } from '@perpay-web/fintech/dataModules/gateCardAccount';
import { gateCardApplyDataModule } from '@perpay-web/fintech/dataModules/gateCardApply';
import { generalAccountSummaryDataModule } from '@perpay-web/fintech/dataModules/generalAccountSummary';
import { marketplacePartnerOnboardingSignupDataModule } from '@perpay-web/fintech/dataModules/marketplacePartnerOnboardingSignup';
import { partnerHostedCardApplicationDataModule } from '@perpay-web/fintech/dataModules/partnerHostedCardApplication';
import { createPasswordDataModule } from '@perpay-web/fintech/dataModules/partnerOnboardingCreatePassword';
import { partnerOnboardingSignupDataModule } from '@perpay-web/fintech/dataModules/partnerOnboardingSignup';
import { partnerOnboardingSubmitCardApplicationDataModule } from '@perpay-web/fintech/dataModules/partnerOnboardingSubmitCardApplication';
import { referralStatsDataModule } from '@perpay-web/fintech/dataModules/referralStats';
import { setStepsForUpdateDirectDepositDataModule } from '@perpay-web/fintech/dataModules/setStepsForUpdateDirectDeposit';
import { updateAutoPayScheduleDataModule } from '@perpay-web/fintech/dataModules/updateAutoPaySchedule';
import { updateCardAccountApplicationDataModule } from '@perpay-web/fintech/dataModules/updateCardAccountApplications';
import { updateCreditLimitDataModule } from '@perpay-web/fintech/dataModules/updateCreditLimit';
import { updateJobDataModule } from '@perpay-web/fintech/dataModules/updateJob';
import { updateUserInfoDataModule } from '@perpay-web/fintech/dataModules/updateUserInfo';
import { updateUserInfoForCardApplicationDataModule } from '@perpay-web/fintech/dataModules/updateUserInfoForCardApplication';
import { updateUserInfoForCardOnboardingMVP2DataModule } from '@perpay-web/fintech/dataModules/updateUserInfoForCardOnboardingMVP2';
import { uploadCardDocumentsDataModule } from '@perpay-web/fintech/dataModules/uploadCardDocuments';

export default combineReducers({
    fetchCardAccounts: fetchCardAccountsDataModule.reducer,
    createCardAccountApplications:
        createCardAccountApplicationsDataModule.reducer,
    generalAccountSummary: generalAccountSummaryDataModule.reducer,
    referralStats: referralStatsDataModule.reducer,
    updateCardAccountApplication:
        updateCardAccountApplicationDataModule.reducer,
    fetchCardAccountApplication: fetchCardAccountApplicationDataModule.reducer,
    fetchCardStatements: fetchCardStatementsDataModule.reducer,
    fetchPaymentSetupSummary: fetchPaymentSetupSummaryDataModule.reducer,
    cardManualPaymentSetup: cardManualPaymentSetupDataModule.reducer,
    directDepositDetails: directDepositDetailsDataModule.reducer,
    fetchCardAccountApplicationForOnboarding:
        fetchCardAccountApplicationForOnboardingDataModule.reducer,
    cardAccountApplicationCrud: cardAccountApplicationCrudDataModule.reducer,
    fetchFeaturesForOnboarding: fetchFeaturesForOnboardingDataModule.reducer,
    fetchCardActivation: fetchCardActivationDataModule.reducer,
    gateCardAccount: gateCardAccountDataModule.reducer,
    gateCardApply: gateCardApplyDataModule.reducer,
    cardStatements: cardStatementsDataModule.reducer,
    downloadCardStatement: downloadCardStatementDataModule.reducer,
    fetchDeductions: fetchDeductionsDataModule.reducer,
    coreSummary: coreSummaryDataModule.reducer,
    fetchUserStatus: fetchUserStatusDataModule.reducer,
    orders: ordersDataModule.reducer,
    creditUnfreezeForCardAccountApplication:
        creditUnfreezeForCardAccountApplicationDataModule.reducer,
    freezeCard: freezeCardDataModule.reducer,
    unfreezeCard: unfreezeCardDataModule.reducer,
    cardOnboardingSwitcher: cardOnboardingSwitcherDataModule.reducer,
    fetchUserInfo: fetchUserInfoDataModule.reducer,
    updateUserInfo: updateUserInfoDataModule.reducer,
    updateUserInfoForCardApplication:
        updateUserInfoForCardApplicationDataModule.reducer,
    updateUserInfoForCardOnboardingMVP2:
        updateUserInfoForCardOnboardingMVP2DataModule.reducer,
    userInfoForCardOnboardingMVP2Crud:
        userInfoForCardOnboardingMVP2CrudDataModule.reducer,
    fetchPayrollInstructions: fetchPayrollInstructionsDataModule.reducer,
    uploadCardDocuments: uploadCardDocumentsDataModule.reducer,
    fetchPinwheelABTest: fetchPinwheelABTestDataModule.reducer,
    fetchJobs: fetchJobsDataModule.reducer,
    cardPaymentSummary: cardPaymentSummaryDataModule.reducer,
    fetchBankAccounts: fetchBankAccountsDataModule.reducer,
    cardCreateAutoPay: cardCreateAutoPayDataModule.reducer,
    createBankAccount: createBankAccountDataModule.reducer,
    createBankAccountForCardAutoPay:
        createBankAccountForCardAutoPayDataModule.reducer,
    fetchBankAccountsForCardAutoPay:
        fetchBankAccountsForCardAutoPayDataModule.reducer,
    bankAccountsForCardAutoPay: bankAccountsForCardAutoPayDataModule.reducer,
    createAutoPaySchedule: createAutoPayScheduleDataModule.reducer,
    createAutoPayScheduleForCardAutoPay:
        createAutoPayScheduleForCardAutoPayDataModule.reducer,
    payrollInstructionsFlow: payrollInstructionsFlowDataModule.reducer,
    cancelAutoPaySchedule: cancelAutoPayScheduleDataModule.reducer,
    fetchAutoPaySchedules: fetchAutoPaySchedulesDataModule.reducer,
    fetchInitiatedAutoPayPayments:
        fetchInitiatedAutoPayPaymentsDataModule.reducer,
    updateAutoPaySchedule: updateAutoPayScheduleDataModule.reducer,
    directDepositUpdateEmployerAndAmount:
        directDepositUpdateEmployerAndAmountDataModule.reducer,
    fetchCardDetailsV2: fetchCardDetailsV2DataModule.reducer,
    directDepositUpdateManualPaymentSetup:
        directDepositUpdateManualPaymentSetupDataModule.reducer,
    createPrimaryJob: createPrimaryJobDataModule.reducer,
    updateJob: updateJobDataModule.reducer,
    updateDirectDeposit: updateDirectDepositDataModule.reducer,
    fetchBorrowerCreditSummary: fetchBorrowerCreditSummaryDataModule.reducer,
    fetchBorrowerCredits: fetchBorrowerCreditsDataModule.reducer,
    borrowerCredits: borrowerCreditsDataModule.reducer,
    fetchFeatureEnrollments: fetchFeatureEnrollmentsDataModule.reducer,
    createFeatureEnrollment: createFeatureEnrollmentDataModule.reducer,
    perpayPlus: perpayPlusDataModule.reducer,
    perpayPlusSolo: perpayPlusSoloDataModule.reducer,
    perpayPlusSoloApplication: perpayPlusSoloApplicationDataModule.reducer,
    fetchFeatureEnrollmentsForPerpayPlus:
        fetchFeatureEnrollmentsForPerpayPlusDataModule.reducer,
    fetchActivePayments: fetchActivePaymentsDataModule.reducer,
    fetchABTests: fetchABTestsDataModule.reducer,
    fetchFeatureSwitches: fetchFeatureSwitchesDataModule.reducer,
    marketplaceDetails: marketplaceDetailsDataModule.reducer,
    fetchCardActivity: fetchCardActivityDataModule.reducer,
    cardActivity: cardActivityDataModule.reducer,
    fetchCreditLimits: fetchCreditLimitsDataModule.reducer,
    updateCreditLimit: updateCreditLimitDataModule.reducer,
    acceptCreditLimit: acceptCreditLimitDataModule.reducer,
    setStepsForUpdateDirectDeposit:
        setStepsForUpdateDirectDepositDataModule.reducer,
    fetchTransactionDetails: fetchTransactionDetailsDataModule.reducer,
    checkout: checkoutDataModule.reducer,
    fetchCardDashboardSummary: fetchCardDashboardSummaryDataModule.reducer,
    fetchMarketplaceDashboardSummary:
        fetchMarketplaceDashboardSummaryDataModule.reducer,
    fetchPlatformDashboardNotifications:
        fetchPlatformDashboardNotificationsDataModule.reducer,
    fetchPerpayPlusDashboardSummary:
        fetchPerpayPlusDashboardSummaryDataModule.reducer,
    fetchDirectDepositDashboardSummary:
        fetchDirectDepositDashboardSummaryDataModule.reducer,
    dashboardV2: dashboardV2DataModule.reducer,
    fetchOrders: fetchOrdersDataModule.reducer,
    fetchLearnMore: fetchLearnMoreDataModule.reducer,
    fetchWithdrawalBalanceV2: fetchWithdrawalBalanceV2DataModule.reducer,
    pinwheel: pinwheelDataModule.reducer,
    fetchCreditScoreHistory: fetchCreditScoreHistoryDataModule.reducer,
    partnerOnboardingSignup: partnerOnboardingSignupDataModule.reducer,
    marketplacePartnerOnboardingSignup:
        marketplacePartnerOnboardingSignupDataModule.reducer,
    createPassword: createPasswordDataModule.reducer,
    partnerOnboardingSubmitCardApplication:
        partnerOnboardingSubmitCardApplicationDataModule.reducer,
    partnerHostedCardApplication:
        partnerHostedCardApplicationDataModule.reducer,
    fetchTrustPilotLink: fetchTrustPilotLinkDataModule.reducer,
    partnerOnboarding: partnerOnboardingDataModule.reducer,
});
